import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { SetupIntent } from '@stripe/stripe-js';
import { Container, Row, Col } from 'react-bootstrap';
import { CheckCircleFill, XCircleFill } from 'react-bootstrap-icons';
import { useAtom } from 'jotai';
import { stripePromise } from './Data/ApiStripe';
import { authAtom } from './Data/Atoms';
import { Button } from './Controls/Button';
import './UserPlanPaidPage.scss';

function statusHeading( s: SetupIntent.Status ): string {
    switch ( s ) {
        case 'succeeded':          return "You're enrolled!";
        case 'processing':         return 'Verification in progress';
        case 'requires_action':    return 'Action required';
        default:                   return 'Enrollment unsuccessful';
    }
}

function statusBody( s: SetupIntent.Status ): string[] {
    switch ( s ) {
        case 'succeeded':
            return [
                'Your payment method has been saved.',
                "You'll be charged automatically based on your parking activity at this facility.",
                "You'll receive a receipt for each transaction.",
            ];
        case 'processing':
            return [
                'Your payment method is being verified. This may take a few moments.',
                "No action is needed — we'll complete your enrollment once verification is done.",
            ];
        case 'requires_action':
            return [
                'Additional verification is needed to confirm your payment method.',
                'Check your email for instructions to complete enrollment.',
            ];
        default:
            return [
                'Your payment method could not be saved.',
                'Please return to the invitation link and try again.',
            ];
    }
}

function isSuccessStatus( s: SetupIntent.Status ): boolean {
    return s === 'succeeded' || s === 'processing' || s === 'requires_action';
}

function EnrollmentStatus( { clientSecret }: { clientSecret: string } ) {
    const stripe = useStripe();
    const nav = useNavigate();
    const [auth] = useAtom( authAtom );
    const [heading, setHeading] = useState<string>();
    const [body, setBody] = useState<string[]>( [] );
    const [success, setSuccess] = useState<boolean>( false );
    const [loaded, setLoaded] = useState( false );

    useEffect( () => {
        if ( !stripe ) return;

        if ( !clientSecret ) {
            setHeading( 'Enrollment unsuccessful' );
            setBody( [
                'Your payment method could not be saved.',
                'Please return to the invitation link and try again.',
            ] );
            setSuccess( false );
            setLoaded( true );
            return;
        }

        stripe.retrieveSetupIntent( clientSecret ).then( ( { setupIntent } ) => {
            if ( !setupIntent ) {
                setHeading( 'Enrollment unsuccessful' );
                setBody( [
                    'Your payment method could not be saved.',
                    'Please return to the invitation link and try again.',
                ] );
                setSuccess( false );
                setLoaded( true );
                return;
            }

            setHeading( statusHeading( setupIntent.status ) );
            setBody( statusBody( setupIntent.status ) );
            setSuccess( isSuccessStatus( setupIntent.status ) );
            setLoaded( true );
        } );
    }, [stripe, clientSecret] );

    if ( !loaded ) {
        return <Container fluid className="px-0 px-sm-2 pt-0 pt-sm-2" />;
    }

    if ( success ) {
        return (
            <Container fluid className="px-0 px-sm-2 pt-0 pt-sm-2">
                <Row className="justify-content-center mt-0 mt-sm-4 g-0">
                    <Col xs={12} md={7} lg={5} className="px-0 px-sm-3">
                        <div className="payment-success-card">
                            <div className="payment-success-header text-center">
                                <div className="payment-success-icon-wrap">
                                    <div className="ring" />
                                    <div className="ring" />
                                    <div className="icon-circle">
                                        <CheckCircleFill color="#D61F84" size={48} />
                                    </div>
                                </div>
                                <div className="payment-success-title">{heading}</div>
                                <div className="payment-success-wave">
                                    <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="white" />
                                    </svg>
                                </div>
                            </div>
                            <div className="payment-success-body text-center">
                                {body.map( ( line, i ) => (
                                    <p key={i} className="payment-success-desc mb-2">{line}</p>
                                ) )}
                                {auth.isLoggedIn && (
                                    <div className="payment-success-btn mt-4">
                                        <Button className="px-5 btn-main" onClick={() => nav( '/user/plan' )}>
                                            View My Plans
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0 px-sm-2 pt-0 pt-sm-2">
            <Row className="justify-content-center mt-0 mt-sm-5 g-0">
                <Col xs={12} md={7} lg={5} className="px-0 px-sm-3">
                    <div className="payment-error-card">
                        <div className="payment-error-header">
                            <XCircleFill size={56} color="white" className="mb-3" />
                            <h3 className="fw-bold mb-0">{heading}</h3>
                        </div>
                        <div className="p-4 text-center bg-white rounded-bottom">
                            {body.map( ( line, i ) => (
                                <p key={i} className="text-secondary mb-2">{line}</p>
                            ) )}
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export function CheckoutPageUsageBasedInviteSuccess() {
    const [searchParams] = useSearchParams();
    const clientSecret = searchParams.get( 'setup_intent_client_secret' ) ?? '';

    return (
        <Elements stripe={stripePromise}>
            <EnrollmentStatus clientSecret={clientSecret} />
        </Elements>
    );
}
